import { useState } from 'react';
import { 
  TextField, 
  Button, 
  Grid, 
  FormControlLabel, 
  Switch, 
  Typography, 
  Container,
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Tooltip,
  useTheme,
  Divider,
  IconButton,
  Snackbar,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import pageSpeedService from '../services/PageSpeedService';
import techDetectionService from '../services/TechnologyDetectionService';
import LighthouseResults from './LighthouseResults';
import TechnologyDetection from './TechnologyDetection';
import LoadingOverlay from './LoadingOverlay';
import issueDetectionService from '../services/IssueDetectionService';
import { Helmet } from 'react-helmet-async';
import { useMetaPixel } from '../hooks/use-meta-pixel';
import { useGoogleAnalytics } from '../hooks/use-google-analytics';
import { useGoogleAds } from '../hooks/use-google-ads';

const PageSpeedAnalyzer = () => {
  const theme = useTheme();
  const { trackWebsiteAnalysis } = useMetaPixel();
  const { trackWebsiteAnalysis: trackGAWebsiteAnalysis } = useGoogleAnalytics();
  const { trackWebsiteAnalysis: trackAdsWebsiteAnalysis } = useGoogleAds();
  
  const [url, setUrl] = useState('');
  const [isMobileAnalysis, setIsMobileAnalysis] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [techData, setTechData] = useState(null);
  const [issueData, setIssueData] = useState(null);
  const [urlError, setUrlError] = useState(false);
  
  // Track the current analysis step
  const [currentStep, setCurrentStep] = useState('initialize');
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [subMessage, setSubMessage] = useState('');
  
  // Add step timing tracking
  const [stepTimes, setStepTimes] = useState({});
  const [stepDurations, setStepDurations] = useState({});

  // Add state for protocol
  const [protocol, setProtocol] = useState('https://');

  // Add state for URL hash
  const [urlHash, setUrlHash] = useState(null);
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [clipboardDenied, setClipboardDenied] = useState(false);

  // Function to update the current step and track timing
  const updateStep = (step) => {
    // Normalize step name - ensure 'initialize' is treated as 'initializing'
    const normalizedStep = step === 'initialize' ? 'initializing' : step;
    
    const now = Date.now();
    
    // If we had a previous step, calculate its duration
    if (currentStep && currentStep !== normalizedStep) {
      if (stepTimes[currentStep]) {
        const duration = now - stepTimes[currentStep];
        
        // Update step durations
        setStepDurations(prev => ({
          ...prev,
          [currentStep]: duration
        }));
      }
    }
    
    // Set the new step
    setCurrentStep(normalizedStep);
    
    // Progress mapping (approximate percentages)
    const progressMap = {
      'initializing': 5,
      'fetching': 20,
      'analyzing': 45,
      'technologies': 60,
      'issues': 75,
      'issues-links': 80,
      'issues-scripts': 85,
      'issues-images': 90,
      'finalizing': 95,
      'complete': 100
    };
    
    // Update progress based on step
    if (progressMap[normalizedStep]) {
      setProgress(progressMap[normalizedStep]);
    }
    
    // Set human-readable messages
    const messageMap = {
      'initializing': 'Preparing analysis...',
      'fetching': 'Fetching page data...',
      'analyzing': 'Running performance analysis...',
      'technologies': 'Detecting technologies...',
      'issues': 'Identifying issues...',
      'issues-links': 'Checking links...',
      'issues-scripts': 'Analyzing scripts...',
      'issues-images': 'Optimizing images...',
      'finalizing': 'Finalizing results...',
      'complete': 'Analysis complete!'
    };
    
    const subMessageMap = {
      'initializing': 'Setting up the analysis environment',
      'fetching': 'Retrieving page content and structure',
      'analyzing': 'Evaluating core web vitals and performance metrics',
      'technologies': 'Identifying frameworks, libraries and tools used',
      'issues': 'Scanning for potential performance issues',
      'issues-links': 'Verifying all page links are working correctly',
      'issues-scripts': 'Checking script loading and execution efficiency',
      'issues-images': 'Verifying image sizes and formats are optimized',
      'finalizing': 'Compiling final report and recommendations',
      'complete': 'Review your detailed performance report below'
    };
    
    if (messageMap[normalizedStep]) {
      setProgressMessage(messageMap[normalizedStep]);
    }
    
    if (subMessageMap[normalizedStep]) {
      setSubMessage(subMessageMap[normalizedStep]);
    }
    
    // Record start time for this step
    setStepTimes(prev => ({
      ...prev,
      [normalizedStep]: now
    }));
  };
  
  // Validate URL format
  const validateUrl = (urlToValidate) => {
    if (!urlToValidate) return false;
    
    // Basic URL format validation - fixed escape characters
    const urlPattern = /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
    return urlPattern.test(urlToValidate);
  };
  
  // Handle URL input change
  const handleInputChange = (e) => {
    let value = e.target.value;
    
    // Strip protocol if user types it in
    if (value.startsWith('https://')) {
      setProtocol('https://');
      value = value.substring(8);
    } else if (value.startsWith('http://')) {
      setProtocol('http://');
      value = value.substring(7);
    }
    
    setUrl(value);
    setUrlError(!validateUrl(`${protocol}${value}`) && value.length > 0);
  };
  
  // Handle protocol change
  const toggleProtocol = () => {
    setProtocol(prev => prev === 'https://' ? 'http://' : 'https://');
  };
  
  // Handle mobile device toggle
  const handleMobileToggle = (e) => {
    setIsMobileAnalysis(e.target.checked);
  };
  
  // Handle form submission to analyze URL
  const handleAnalyze = async () => {
    // Clear previous errors
    setError(null);
    
    // Validate URL
    if (!url) {
      setError("Please enter a website URL to analyze");
      return;
    }
    
    try {
      // Use the selected protocol
      let validUrl = `${protocol}${url}`;
      
      // Track website analysis initiation with Meta Pixel
      trackWebsiteAnalysis({
        url: validUrl,
        analysis_type: isMobileAnalysis ? 'mobile' : 'desktop',
        source: 'public_analyzer'
      });

      // Track website analysis initiation with Google Analytics
      trackGAWebsiteAnalysis({
        url: validUrl,
        analysis_type: isMobileAnalysis ? 'mobile' : 'desktop',
        source: 'public_analyzer'
      });

      // Track website analysis initiation with Google Ads
      trackAdsWebsiteAnalysis({
        url: validUrl,
        analysis_type: isMobileAnalysis ? 'mobile' : 'desktop',
        source: 'public_analyzer',
        value: 5, // Assign value to analysis conversion
        currency: 'USD'
      });
      
      // Reset the view and status
      setResult(null);
      setTechData(null);
      setIssueData(null);
      setProgress(0);
      setStepTimes({});
      setStepDurations({});
      setCurrentStep('initialize');
      
      // Start fresh with timing data
      const startTime = Date.now();
      const timingData = {
        overallStartTime: startTime
      };
      
      // Start with initializing step
      updateStep('initializing');
      setLoading(true);
      timingData['initializing'] = { startTime };
      
      // Small delay to ensure UI updates before proceeding
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Analyze performance with Lighthouse
      try {
        // Record end of initializing step
        const initEndTime = Date.now();
        timingData['initializing'].endTime = initEndTime;
        timingData['initializing'].duration = initEndTime - timingData['initializing'].startTime;
        
        // Start fetching step
        updateStep('fetching');
        timingData['fetching'] = { startTime: initEndTime };
        
        // Small delay to ensure UI updates for step change
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const { response } = await pageSpeedService.analyzeUrl(validUrl, { 
          mobile: isMobileAnalysis,
          category: ['performance', 'accessibility', 'best-practices', 'seo', 'pwa'],
          localOnly: false
        });
        
        // Record end of fetching step
        const fetchEndTime = Date.now();
        timingData['fetching'].endTime = fetchEndTime;
        timingData['fetching'].duration = fetchEndTime - timingData['fetching'].startTime;
        
        // Start analyzing step with small delay for UI update
        updateStep('analyzing');
        await new Promise(resolve => setTimeout(resolve, 100));
        timingData['analyzing'] = { startTime: fetchEndTime };
        
        // Process the results
        setResult(response);
        
        // Record end of analyzing step
        const analyzeEndTime = Date.now();
        timingData['analyzing'].endTime = analyzeEndTime;
        timingData['analyzing'].duration = analyzeEndTime - timingData['analyzing'].startTime;
        
        // Start technology detection
        updateStep('technologies');
        await new Promise(resolve => setTimeout(resolve, 100));
        timingData['technologies'] = { startTime: analyzeEndTime };
        
        // Detect technologies used on the website
        const techResults = await techDetectionService.detectTechnologies(validUrl, {
          timeoutMs: 30000,
          raw: true,
          analysisData: response
        });
        
        setTechData(techResults);
        
        // Record end of technology detection step
        const techEndTime = Date.now();
        timingData['technologies'].endTime = techEndTime;
        timingData['technologies'].duration = techEndTime - timingData['technologies'].startTime;
        
        // Start issue detection
        updateStep('issues');
        await new Promise(resolve => setTimeout(resolve, 100));
        timingData['issues'] = { startTime: techEndTime };
        
        // Wait a bit for UI to update
        const issueStartTime = Date.now();
        
        // Record end of generic issues step
        timingData['issues'].endTime = issueStartTime;
        timingData['issues'].duration = issueStartTime - timingData['issues'].startTime;
        
        // Handle issue detection with substeps
        updateStep('issues-links');
        await new Promise(resolve => setTimeout(resolve, 100));
        timingData['issues-links'] = { startTime: issueStartTime };
        
        const issueResults = await issueDetectionService.detectAllIssues(validUrl, response, {
          onBrokenLinksStart: () => {
            updateStep('issues-links');
          },
          onBrokenLinksComplete: () => {
            // Record end of links step
            const linksEndTime = Date.now();
            timingData['issues-links'].endTime = linksEndTime;
            timingData['issues-links'].duration = linksEndTime - timingData['issues-links'].startTime;
          },
          onScriptIssuesStart: () => {
            updateStep('issues-scripts');
            setTimeout(() => {
              console.log('Script issues detection started');
            }, 100);
            timingData['issues-scripts'] = { startTime: Date.now() };
          },
          onScriptIssuesComplete: () => {
            // Record end of scripts step
            const scriptsEndTime = Date.now();
            timingData['issues-scripts'].endTime = scriptsEndTime;
            timingData['issues-scripts'].duration = scriptsEndTime - timingData['issues-scripts'].startTime;
          },
          onImageIssuesStart: () => {
            updateStep('issues-images');
            setTimeout(() => {
              console.log('Image issues detection started');
            }, 100);
            timingData['issues-images'] = { startTime: Date.now() };
          },
          onImageIssuesComplete: () => {
            // Record end of images step
            const imagesEndTime = Date.now();
            timingData['issues-images'].endTime = imagesEndTime;
            timingData['issues-images'].duration = imagesEndTime - timingData['issues-images'].startTime;
          }
        });
        
        console.log('Issue detection completed with results:', {
          brokenLinks: issueResults.brokenLinks?.length || 0,
          scriptIssues: issueResults.scriptIssues?.length || 0,
          imageIssues: issueResults.imageIssues?.length || 0
        });
        
        setIssueData(issueResults);
        
        // Start finalizing step
        updateStep('finalizing');
        await new Promise(resolve => setTimeout(resolve, 100));
        timingData['finalizing'] = { startTime: Date.now() };
        
        // Record overall duration
        const endTime = Date.now();
        const overallDuration = endTime - startTime;
        
        // Save analysis only after all checks are successful
        if (!response.localOnly && response && response.rawData) {
          try {
            // Enhance rawData with additional information
            const enhancedResponse = {
              ...response,
              rawData: {
                ...response.rawData,
                // Add technology detection results
                technologies: {
                  timestamp: new Date().toISOString(),
                  results: techResults
                },
                // Add issue detection results
                issues: {
                  timestamp: new Date().toISOString(),
                  brokenLinks: issueResults.brokenLinks || [],
                  scriptIssues: issueResults.scriptIssues || [],
                  imageIssues: issueResults.imageIssues || []
                },
                // Add timing information
                timing: {
                  overallDuration,
                  stepDurations
                }
              }
            };

            const saveResult = await pageSpeedService.saveAnalysis(enhancedResponse);
            console.log('saveResult:', saveResult);
            console.log('Analysis saved to server successfully');
            // Store the URL hash for sharing
            setUrlHash(saveResult.url_hash);
          } catch (saveError) {
            console.error('Failed to save analysis to server:', saveError);
            // Continue despite save error - user still gets results
          }
        }

        console.log(`Analysis completed in ${overallDuration}ms`);
        console.log('Step durations:', stepDurations);
        
        // Complete the analysis
        updateStep('complete');
        await new Promise(resolve => setTimeout(resolve, 100));
        
      } catch (apiError) {
        console.error('API Error:', apiError);
        // Extract just the error message, remove any "Error:" prefix
        const errorMessage = JSON.parse(apiError.message.replace('PageSpeed API Error: ', '')).error.message || 'Unable to complete the analysis';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
      
    } catch (err) {
      console.error('Error during analysis:', err);
      // Extract just the error message, remove any "Error:" prefix
      const errorMessage = err.message?.replace(/^Error:\s*/i, '') || 'Unable to complete the analysis';
      setError(errorMessage);
      setLoading(false);
    }
  };

  // Handle Enter key press in URL input field
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading && url && !urlError) {
      handleAnalyze();
    }
  };

  // Add function to handle sharing
  const handleShare = async () => {
    if (isSharing) return;
    
    const shareUrl = `${window.location.origin}/result/${urlHash}`;
    
    try {
      setIsSharing(true);
      setClipboardDenied(false);
      
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'Website Analysis Results',
            text: 'Check out these website analysis results!',
            url: shareUrl
          });
        } catch (shareError) {
          if (shareError.name !== 'AbortError') {
            try {
              await navigator.clipboard.writeText(shareUrl);
              setShowCopySuccess(true);
            } catch (clipboardError) {
              console.warn('Clipboard access denied:', clipboardError);
              setClipboardDenied(true);
            }
          }
        }
      } else {
        try {
          await navigator.clipboard.writeText(shareUrl);
          setShowCopySuccess(true);
        } catch (clipboardError) {
          console.warn('Clipboard access denied:', clipboardError);
          setClipboardDenied(true);
        }
      }
    } catch (err) {
      console.error('Error sharing:', err);
    } finally {
      setIsSharing(false);
    }
  };

  // Add function to handle field click/selection
  const handleFieldClick = async (e) => {
    e.target.select(); // Select the entire text
    if (!isSharing) {
      try {
        setClipboardDenied(false);
        await navigator.clipboard.writeText(e.target.value);
        setShowCopySuccess(true);
      } catch (clipboardError) {
        console.warn('Clipboard access denied:', clipboardError);
        setClipboardDenied(true);
      }
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
      <Helmet>
        <title>Website Performance & Technology Analysis | Gravity Point Media</title>
        <meta name="description" content="Comprehensive website analysis tool for performance, technology stack detection, and issue identification. Analyze speed, tech stack, broken links, script issues, and image optimization." />
      </Helmet>
      
      <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 5 } }}>
        <Typography 
          variant="h2" 
          component="h1" 
          gutterBottom
          sx={{ 
            fontWeight: 700, 
            fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' },
            mb: 2
          }}
        >
          Website Analysis Suite
        </Typography>
        <Typography 
          variant="subtitle1" 
          color="text.secondary"
          sx={{ 
            maxWidth: 800, 
            mx: 'auto',
            fontSize: { xs: '1rem', md: '1.1rem' },
            lineHeight: 1.5
          }}
        >
          Analyze your website's performance, detect technology stack, and identify optimization opportunities. 
          Our comprehensive analysis covers speed metrics, tech detection, broken links, script issues, and image optimization.
        </Typography>
      </Box>
      
      <Card 
        elevation={2} 
        sx={{ 
          borderRadius: 3,
          mb: 4,
          overflow: 'visible'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative' }}>
                <TextField
                  label="Enter website URL"
                  placeholder="example.com"
                  variant="outlined"
                  fullWidth
                  value={url}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  error={urlError}
                  helperText={urlError ? "Please enter a valid URL" : ""}
                  disabled={loading}
                  InputProps={{
                    sx: {
                      paddingLeft: '94px',
                      borderRadius: 2
                    },
                    // Add an invisible placeholder to prevent floating label overlap
                    startAdornment: <Box component="span" sx={{ visibility: 'hidden', width: 0 }}></Box>
                  }}
                  InputLabelProps={{
                    shrink: true,
                    sx: {
                      ml: 1.5 // Adjust label position
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    },
                    '& .MuiFormHelperText-root': {
                      ml: 12, // Align error message with input text (94px + left padding)
                      position: 'absolute',
                      mt: 0.5,
                      bottom: '-22px'
                    },
                    '& .MuiInputLabel-root': {
                      padding: '0 15px 0 0',
                      backgroundColor: '#fff'
                    }
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: 2,
                    color: 'text.secondary',
                    zIndex: 1
                  }}
                >
                  <Box 
                    component="span" 
                    onClick={toggleProtocol}
                    sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      transition: 'color 0.2s',
                      color: protocol === 'https://' ? theme.palette.success.main : theme.palette.warning.main,
                      '&:hover': {
                        color: protocol === 'https://' ? theme.palette.success.dark : theme.palette.warning.dark,
                      }
                    }}
                  >
                    <Tooltip title={`Switch to ${protocol === 'https://' ? 'http://' : 'https://'}`}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {protocol === 'https://' ? (
                          <>
                            <LockIcon fontSize="small" sx={{ mr: 0.5 }} />
                            https://
                          </>
                        ) : (
                          <>
                            <LockOpenIcon fontSize="small" sx={{ mr: 0.5 }} />
                            http://
                          </>
                        )}
                      </Box>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isMobileAnalysis}
                      onChange={handleMobileToggle}
                      disabled={loading}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {isMobileAnalysis ? (
                        <>
                          <PhoneAndroidIcon fontSize="small" sx={{ mr: 0.5 }} />
                          <Typography variant="body2">Mobile Analysis</Typography>
                        </>
                      ) : (
                        <>
                          <DesktopWindowsIcon fontSize="small" sx={{ mr: 0.5 }} />
                          <Typography variant="body2">Desktop Analysis</Typography>
                        </>
                      )}
                    </Box>
                  }
                  sx={{ mr: 2 }}
                />
                <Tooltip title="Toggle between mobile and desktop analysis modes. Mobile analysis simulates a mobile device to test how your site performs on smartphones.">
                  <InfoIcon fontSize="small" color="action" />
                </Tooltip>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleAnalyze}
                disabled={!url || loading || urlError}
                startIcon={<SearchIcon />}
                sx={{ 
                  py: 1.5,
                  fontWeight: 600
                }}
              >
                Analyze Website Page
              </Button>
            </Grid>
          </Grid>
          
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mt: 2, 
                borderRadius: 2,
                '& .MuiAlert-message': {
                  width: '100%'
                }
              }}
              action={
                <Button 
                  color="inherit" 
                  size="small" 
                  onClick={() => setError(null)}
                >
                  Dismiss
                </Button>
              }
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Analysis Failed
              </Typography>
              <Typography variant="body2">
                {error}
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>
      
      {loading && (
        <LoadingOverlay 
          progress={progress} 
          message={progressMessage} 
          subMessage={subMessage}
        />
      )}
      
      {result && !loading && (
        <Box mt={4}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                mb: 3
              }}>
                <Typography 
                  variant="h4" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    mb: 0,
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: 600
                  }}
                >
                  Analysis Results
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {urlHash && (
                    <Card sx={{ p: 1, bgcolor: 'background.paper' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          size="small"
                          value={`${window.location.origin}/result/${urlHash}`}
                          onClick={handleFieldClick}
                          onFocus={handleFieldClick}
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip title={clipboardDenied ? "Please manually copy the URL (Ctrl/Cmd + C)" : "Click anywhere in this field to copy"}>
                                  <IconButton
                                    edge="end"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleShare();
                                    }}
                                    disabled={isSharing}
                                    sx={{ color: clipboardDenied ? 'warning.main' : 'primary.main' }}
                                  >
                                    {isSharing ? (
                                      <CircularProgress size={20} color="inherit" />
                                    ) : (
                                      navigator.share ? <ShareIcon /> : <ContentCopyIcon />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ),
                            sx: {
                              cursor: 'text',
                              bgcolor: 'background.default',
                              '&:hover': {
                                bgcolor: 'action.hover'
                              }
                            }
                          }}
                          sx={{ 
                            minWidth: 300,
                            '& .MuiOutlinedInput-root': {
                              cursor: 'text',
                              '&:hover': {
                                bgcolor: 'action.hover'
                              }
                            }
                          }}
                        />
                      </Box>
                    </Card>
                  )}
                  <Chip 
                    label={isMobileAnalysis ? "Mobile" : "Desktop"} 
                    color={isMobileAnalysis ? "secondary" : "primary"} 
                    size="small" 
                    icon={isMobileAnalysis ? <PhoneAndroidIcon /> : <DesktopWindowsIcon />}
                  />
                </Box>
              </Box>
              <Divider sx={{ mb: 4 }} />
              
              <LighthouseResults 
                data={result} 
                isMobile={isMobileAnalysis}
                techData={techData}
                issueData={issueData}
              />
            </Grid>
          </Grid>
        </Box>
      )}
      
      {techData && !loading && (
        <Box mt={4}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom
                sx={{ mb: 3, fontWeight: 600 }}
              >
                Technology Stack
              </Typography>
              <Divider sx={{ mb: 4 }} />
              
              <TechnologyDetection data={techData} />
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Add Snackbar for copy success */}
      <Snackbar
        open={showCopySuccess || clipboardDenied}
        autoHideDuration={5000}
        onClose={() => {
          setShowCopySuccess(false);
          setClipboardDenied(false);
        }}
        message={clipboardDenied 
          ? "Clipboard access denied. Please use Ctrl/Cmd + C to copy manually." 
          : "Share link copied to clipboard!"
        }
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            bgcolor: clipboardDenied ? 'warning.dark' : 'success.main'
          }
        }}
      />
    </Container>
  );
};

export default PageSpeedAnalyzer;